import { useState } from 'react';
import { api, ApiError, errorMessage } from '../api';
import { readVerifyMode, resendVerification, writeVerifyMode } from '../auth';
import { Btn, Card, Field, Icon, Notice } from '../components/ui';
import type { IconName } from '../components/ui';
import { usePortal } from '../App';

/** El código de correo son 6 dígitos — se filtra todo lo demás al teclear. */
const onlyDigits = (raw: string) => raw.replace(/\D/g, '').slice(0, 6);

/** Icono circular de las tarjetas de espera (mismo lenguaje que Empty). */
function GateIcon({ name }: { name: IconName }) {
  return <span className="gate-ic"><Icon name={name} size={26} /></span>;
}

/** Pantallas de espera para sesiones que aún no entran:
    correo sin verificar → solicitud de acceso (joinGroup) →
    pendiente de autorización · acceso suspendido. */
export function Gate() {
  const { session, token, busy, refresh } = usePortal();
  const [code, setCode] = useState('');
  const [joinName, setJoinName] = useState(session.displayName);
  const [joinBusy, setJoinBusy] = useState(false);
  const [localMsg, setLocalMsg] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  // 'code' = código de 6 dígitos del servicio (emulador/selfhost); 'link' =
  // correo con enlace de Auth (la nube no expone las acciones de código).
  const [verifyMode, setVerifyMode] = useState<'code' | 'link'>(() => readVerifyMode() ?? 'code');

  const sendCode = async () => {
    setLocalMsg(null);
    try {
      const mode = await resendVerification(token);
      setVerifyMode(mode); writeVerifyMode(mode);
      setLocalMsg({ tone: 'info', text: mode === 'code'
        ? `Enviamos un código de 6 dígitos a ${session.email}.`
        : `Te enviamos un correo con el enlace de verificación a ${session.email}. Ábrelo y después toca «Comprobar».` });
    } catch (e) {
      // Ya verificado no es error: la recarga muestra la tarjeta siguiente.
      const verified = e instanceof ApiError && e.code === 'ALREADY_VERIFIED';
      setLocalMsg({ tone: verified ? 'info' : 'danger', text: errorMessage(e) });
    }
  };

  /** Verificado → solicitud de acceso en el mismo paso (joinGroup con código
      vacío = registro abierto: el miembro queda 'pending' para el administrador). */
  const joinGroup = async (displayName: string): Promise<boolean> => {
    try {
      await api('joinGroup', { code: '', displayName }, token);
      return true;
    } catch (e) { setLocalMsg({ tone: 'danger', text: errorMessage(e) }); return false; }
  };

  const confirm = async () => {
    setLocalMsg(null);
    try {
      await api('confirmEmailCode', { code: code.trim() }, token);
    } catch (e) {
      // La nube no expone códigos: la verificación llega como enlace — el
      // reenvío cambia la tarjeta a ese modo y manda el correo correcto.
      if (e instanceof ApiError && e.code === 'INVALID_ACTION') { await sendCode(); return; }
      setLocalMsg({ tone: 'danger', text: errorMessage(e) }); return;
    }
    // Correo confirmado: la solicitud entra sola a la cola del administrador.
    // Si falla (p. ej. red), la recarga deja la tarjeta «Solicita tu acceso».
    await joinGroup(session.displayName);
    await refresh();
  };

  const join = async () => {
    const name = joinName.trim().slice(0, 80);
    if (name.length < 2 || joinBusy) return;
    setJoinBusy(true); setLocalMsg(null);
    try { if (await joinGroup(name)) await refresh(); }
    finally { setJoinBusy(false); }
  };

  if (!session.emailVerified) {
    const byLink = verifyMode === 'link';
    return <Card className="gate">
      <GateIcon name="mail" />
      <h2 className="card-title">Verifica tu correo</h2>
      <p className="gate-desc">
        {byLink
          ? <>Antes de entrar confirma que <b>{session.email}</b> es tuyo: te enviamos un correo con un enlace de verificación. Ábrelo y vuelve aquí. Si no lo ves, revisa correo no deseado o pídelo de nuevo.</>
          : <>Antes de entrar confirma que <b>{session.email}</b> es tuyo: te enviamos un código de 6 dígitos. Si no lo ves, revisa correo no deseado o pide uno nuevo.</>}
      </p>
      {localMsg && <Notice tone={localMsg.tone}>{localMsg.text}</Notice>}
      {!byLink && <Field label="Código de verificación" value={code} onChange={e => setCode(onlyDigits(e.target.value))}
        placeholder="6 dígitos" maxLength={6} inputMode="numeric" autoComplete="one-time-code" />}
      <div className="actions">
        {byLink
          ? <Btn busy={busy} onClick={() => void refresh()}>Comprobar verificación</Btn>
          : <Btn busy={busy} disabled={code.trim().length !== 6} onClick={() => void confirm()}>Confirmar código</Btn>}
        <Btn tone="secondary" busy={busy} onClick={() => void sendCode()}>{byLink ? 'Reenviar correo' : 'Enviar código nuevo'}</Btn>
        {!byLink && <Btn tone="ghost" busy={busy} onClick={() => void refresh()}>Ya verifiqué mi correo</Btn>}
      </div>
    </Card>;
  }

  if (session.status === 'blocked') {
    return <Card className="gate">
      <GateIcon name="lock" />
      <h2 className="card-title">Acceso suspendido</h2>
      <p className="gate-desc">Tu cuenta está suspendida. Habla con el administrador para revisar tu situación.</p>
      <div className="actions"><Btn tone="secondary" busy={busy} onClick={() => void refresh()}>Comprobar acceso</Btn></div>
    </Card>;
  }

  // Correo verificado pero sin solicitud aún (cuentas creadas antes del registro
  // abierto o una joinGroup que no llegó): la cola del admin solo la forma un
  // joinGroup real.
  if (!session.inviteValidated) {
    return <Card className="gate">
      <GateIcon name="key" />
      <h2 className="card-title">Solicita tu acceso</h2>
      <p className="gate-desc">Hola, {session.displayName || 'operador'}. Tu correo ya está verificado; manda tu solicitud y un administrador autoriza tu entrada.</p>
      {localMsg && <Notice tone={localMsg.tone}>{localMsg.text}</Notice>}
      <Field label="Tu nombre" value={joinName} onChange={e => setJoinName(e.target.value)}
        maxLength={80} autoCapitalize="words" hint="Como te verá el administrador en la lista de solicitudes." />
      <div className="actions">
        <Btn busy={busy || joinBusy} disabled={joinName.trim().length < 2} onClick={() => void join()}>Solicitar acceso</Btn>
        <Btn tone="ghost" busy={busy} onClick={() => void refresh()}>Comprobar autorización</Btn>
      </div>
    </Card>;
  }

  return <Card className="gate">
    <GateIcon name="clock" />
    <h2 className="card-title">Solicitud en revisión</h2>
    <p className="gate-desc">Hola, {session.displayName || 'operador'}. Un administrador autorizará tu acceso al directorio.</p>
    <Notice tone="info" icon="clock">Te avisaremos en esta pantalla: toca «Comprobar autorización» cuando te confirmen.</Notice>
    <div className="actions"><Btn tone="secondary" busy={busy} onClick={() => void refresh()}>Comprobar autorización</Btn></div>
  </Card>;
}
